import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';
import { Menu, User, Plus, Calendar, Search, Tag, Package, Palette, AlertTriangle, Check, Clock, Smartphone, Banknote, Camera, X, Wallet, ShoppingCart } from 'lucide-react';
import { PRODUCT_ICONS, getTextColor } from './Products';

export default function Dashboard() {
  const navigate = useNavigate();
  const { setShowMenu } = useOutletContext();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [saleQuantity, setSaleQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [providerDebt, setProviderDebt] = useState(0);
  
  // Custom work state
  const [customForm, setCustomForm] = useState({
    client_id: '',
    description: '',
    price: '',
    cost: '',
    stock_ids: [],
    photos: []
  });
  const fileInputRef = useRef(null);

  const isEstetica = user?.theme === 'estetica';
  const isVendedor = user?.business_type === 'Vendedor';

  // Digital clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => { 
    if (user?.id) { 
      fetchProducts(); 
      fetchClients(); 
      fetchLowStock();
      fetchStock();
      fetchUrgentPending();
      api.get(`/provider-debt/${user.id}`).then(r => setProviderDebt(r.data.total_remaining_debt || 0)).catch(() => {});
    } 
  }, [user]);

  useEffect(() => {
    if (!searchQuery.trim()) setFilteredProducts(products);
    else setFilteredProducts(products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())));
  }, [searchQuery, products]);

  const fetchProducts = async () => {
    try { const r = await api.get(`/products/${user.id}`); setProducts(r.data); setFilteredProducts(r.data); }
    catch (e) { console.error(e); }
  };

  const fetchClients = async () => {
    try { const r = await api.get(`/clients/${user.id}`); setClients(r.data); }
    catch (e) { console.error(e); }
  };

  const fetchLowStock = async () => {
    try { const r = await api.get(`/stock/${user.id}/low-stock`); setLowStockItems(r.data); }
    catch (e) { console.error(e); }
  };

  const fetchStock = async () => {
    try { const r = await api.get(`/stock/${user.id}`); setStockItems(r.data); }
    catch (e) { console.error(e); }
  };

  const handleProductPress = (product) => {
    setSelectedProduct(product);
    // Check stock warning for stock-linked products
    if (product.stock_id) {
      const stockItem = stockItems.find(s => s.id === product.stock_id);
      setStockWarning(stockItem && stockItem.quantity === 0 ? {
        name: stockItem.description,
        quantity: stockItem.quantity,
      } : null);
    } else {
      setStockWarning(null);
    }
    if (product.is_custom) {
      setCustomForm({ client_id: '', description: '', price: '', cost: '', stock_ids: [], photos: [] });
      setPaymentMethod('');
      setIsCompleted(false);
      setIsPaid(false);
      setShowCustomModal(true);
    } else {
      setIsCompleted(false); setIsPaid(false); setPaymentMethod(''); setSelectedClient(null); setSaleQuantity(1);
      setShowSaleModal(true);
    }
  };

  const [paymentAlert, setPaymentAlert] = useState(false);
  const [urgentPendingCount, setUrgentPendingCount] = useState(0);
  const [stockWarning, setStockWarning] = useState(null);

  const fetchUrgentPending = async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/pending-expenses/${user.id}`);
      const items = r.data;
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
      const tomorrow = new Date(today + 'T12:00:00');
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toLocaleDateString('en-CA');
      setUrgentPendingCount(items.filter(i => i.due_date <= tomorrowStr).length);
    } catch (e) { console.error(e); }
  };

  const handleRegisterSale = async () => {
    if (!isCompleted) { alert('Debes marcar el trabajo como REALIZADO'); return; }
    if (isPaid && !paymentMethod) { 
      setPaymentAlert(true);
      setTimeout(() => setPaymentAlert(false), 3000);
      return; 
    }
    if (!isPaid && !selectedClient) { alert('Debes seleccionar un cliente para cuenta corriente'); return; }
    setLoading(true);
    try {
      await api.post('/sales', {
        business_user_id: user.id,
        product_id: selectedProduct.id,
        client_id: selectedClient,
        is_completed: isCompleted,
        is_paid: isPaid,
        payment_method: paymentMethod,
        quantity: saleQuantity,
      });
      alert('Venta registrada exitosamente');
      setShowSaleModal(false);
      fetchLowStock();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al registrar venta');
    } finally { setLoading(false); }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCustomForm(prev => ({ ...prev, photos: [...prev.photos, ev.target.result] }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setCustomForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  const toggleStockItem = (stockId) => {
    setCustomForm(prev => ({
      ...prev,
      stock_ids: prev.stock_ids.includes(stockId) 
        ? prev.stock_ids.filter(id => id !== stockId)
        : [...prev.stock_ids, stockId]
    }));
  };

  const handleRegisterCustomWork = async () => {
    if (!customForm.client_id) { alert('Debes seleccionar un cliente'); return; }
    if (!customForm.price) { alert('Debes ingresar el precio'); return; }
    if (!isCompleted) { alert('Debes marcar el trabajo como REALIZADO'); return; }
    if (isPaid && !paymentMethod) {
      setPaymentAlert(true);
      setTimeout(() => setPaymentAlert(false), 3000);
      return;
    }

    setLoading(true);
    try {
      await api.post('/custom-work', {
        business_user_id: user.id,
        client_id: customForm.client_id,
        description: customForm.description || null,
        price: parseFloat(customForm.price),
        cost: customForm.cost ? parseFloat(customForm.cost) : 0,
        stock_items: customForm.stock_ids,
        photos: customForm.photos,
        payment_method: isPaid ? paymentMethod : null,
        is_paid: isPaid,
        is_completed: isCompleted,
      });
      alert('Trabajo personalizado registrado');
      setShowCustomModal(false);
      fetchLowStock();
      fetchStock();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (detail || 'Error al registrar');
      alert(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="page-full">
      {/* Header */}
      <div className="header">
        <button className="icon-btn" data-testid="menu-btn" onClick={() => setShowMenu(true)}><Menu size={24} strokeWidth={1.5} /></button>
        <div className="header-center">
          <div className="header-title">{user?.business_name}</div>
          <div className="header-subtitle">{user?.business_type}</div>
        </div>
        <div className="digital-clock">
          {currentTime.toLocaleTimeString('es-AR', { 
            timeZone: 'America/Argentina/Buenos_Aires',
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit', 
            hour12: false 
          })}
        </div>
        <button className="icon-btn" data-testid="profile-btn" onClick={() => navigate('/business/profile')}>
          {user?.profile_image ? (
            <img src={user.profile_image} alt="" className="header-avatar" />
          ) : (
            <div className="header-avatar-placeholder"><User size={24} strokeWidth={1.5} /></div>
          )}
        </button>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="low-stock-alert" style={{ margin: '16px', marginBottom: '0' }} data-testid="dashboard-low-stock-alert">
          <div className="alert-icon"><AlertTriangle size={28} strokeWidth={1.5} /></div>
          <div className="alert-content">
            <strong>¡Stock Bajo!</strong>
            <p>{lowStockItems.length} producto(s) con poco stock</p>
            <div className="alert-items">
              {lowStockItems.slice(0, 3).map(item => (
                <span key={item.id} className="alert-item-tag">{item.description}: {item.quantity}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Urgent Pending Expenses Alert */}
      {urgentPendingCount > 0 && (
        <div
          data-testid="dashboard-urgent-expense-alert"
          onClick={() => navigate('/business/pending-expenses')}
          style={{
            margin: '12px 16px 0',
            background: 'rgba(255,82,82,0.1)',
            border: '1px solid #ff5252',
            borderRadius: 12,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
          }}
        >
          <AlertTriangle size={22} color="#ff5252" strokeWidth={1.5} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#ff5252', fontSize: '14px' }}>
              {urgentPendingCount} gasto(s) próximo(s) a vencer
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 2 }}>
              Tocá para ver los detalles
            </div>
          </div>
          <span style={{ color: '#ff5252', fontSize: '18px', fontWeight: 700 }}>›</span>
        </div>
      )}

      {/* Provider Debt Alert */}
      {providerDebt > 0 && (
        <div
          data-testid="dashboard-provider-debt-alert"
          onClick={() => navigate('/business/purchases')}
          style={{
            margin: '12px 16px 0',
            background: 'rgba(39,174,96,0.08)',
            border: '1px solid rgba(39,174,96,0.4)',
            borderRadius: 12,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
          }}
        >
          <ShoppingCart size={20} color="#27AE60" strokeWidth={1.5} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#27AE60', fontSize: '14px' }}>
              Deuda pendiente con proveedores
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 2 }}>
              Tocá para ver las compras
            </div>
          </div>
          <span style={{ fontWeight: 800, color: '#27AE60', fontSize: '15px' }}>
            ${(providerDebt || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
          </span>
          <span style={{ color: '#27AE60', fontSize: '18px', fontWeight: 700 }}>›</span>
        </div>
      )}

      {/* Search */}
      <div className="search-container">
        <div className="search-input-wrap">
          <span className="search-icon"><Search size={18} strokeWidth={1.5} /></span>
          <input className="search-input" placeholder="Buscar productos/servicios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ padding: '0 16px', marginBottom: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <button
            className="action-btn action-btn-primary"
            style={{ flex: 1 }}
            data-testid="new-product-btn"
            onClick={() => navigate('/business/products')}
          >
            <Plus size={18} strokeWidth={2} /> Crear Nuevo Botón Producto/Servicio
          </button>
          <button
            className="action-btn action-btn-secondary"
            style={{ flex: 1 }}
            data-testid="appointments-btn"
            onClick={() => navigate(isVendedor ? '/business/catalog' : '/business/appointments')}
          >
            <Calendar size={18} strokeWidth={1.5} /> {isVendedor ? 'Catálogo' : 'Turnos'}
          </button>
        </div>
        <button
          className="action-btn"
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, #FF8C42, #FFAA00)',
            color: '#000',
            boxShadow: '0 4px 15px rgba(255,140,66,0.28)',
            fontWeight: 700,
          }}
          data-testid="register-expense-btn"
          onClick={() => navigate('/business/expenses')}
        >
          <Wallet size={18} strokeWidth={1.5} /> Registrar Gasto
        </button>
      </div>

      {/* Products Grid */}
      <div className="content-scroll">
        <h2 className="section-title">Productos y Servicios</h2>
        <div className="products-grid">
            {filteredProducts.map(p => {
            const textColor = p.color ? getTextColor(p.color) : undefined;
            const iconEntry = p.icon ? PRODUCT_ICONS.find(i => i.key === p.icon) : null;

            // Build gradient + relief style when color is set (only when no button_image)
            const colorStyle = (() => {
              if (!p.color || p.button_image) return {};
              const r = parseInt(p.color.slice(1,3), 16);
              const g = parseInt(p.color.slice(3,5), 16);
              const b = parseInt(p.color.slice(5,7), 16);
              const lighter = `rgb(${Math.min(255,r+45)},${Math.min(255,g+45)},${Math.min(255,b+45)})`;
              const darker  = `rgb(${Math.max(0,r-30)},${Math.max(0,g-30)},${Math.max(0,b-30)})`;
              return {
                background: `linear-gradient(145deg, ${lighter} 0%, ${p.color} 50%, ${darker} 100%)`,
                boxShadow: [
                  'inset 0 1px 1px rgba(255,255,255,0.30)',
                  'inset 0 -2px 3px rgba(0,0,0,0.18)',
                  `0 6px 16px ${p.color}66`,
                  '0 2px 5px rgba(0,0,0,0.35)',
                ].join(', '),
                border: 'none',
                color: textColor,
              };
            })();

            // Image button style
            const imageStyle = p.button_image ? {
              padding: 0,
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25), 0 2px 5px rgba(0,0,0,0.18)',
              border: p.color ? `2px solid ${p.color}` : '1px solid var(--border)',
            } : {};

            return (
              <button
                key={p.id}
                className={`product-card ${p.is_custom ? 'custom-product' : ''} ${p.color && !p.button_image ? 'has-color' : ''}`}
                style={p.button_image ? imageStyle : colorStyle}
                onClick={() => handleProductPress(p)}
              >
                {p.button_image ? (
                  // ── Image button layout ──
                  <>
                    <img
                      src={p.button_image}
                      alt={p.name}
                      style={{ width: '100%', height: 82, objectFit: 'cover', display: 'block', borderRadius: p.color ? '10px 10px 0 0' : '12px 12px 0 0' }}
                    />
                    <div style={{ padding: '7px 8px 8px', background: p.color || 'var(--surface)' }}>
                      <div className="product-name" style={{ fontSize: '13px', color: p.color ? getTextColor(p.color) : undefined }}>
                        {p.name}
                      </div>
                      {p.is_custom ? (
                        <div className="product-price" style={{ fontSize: '10px', color: p.color ? getTextColor(p.color) : undefined, opacity: 0.8 }}>Personalizado</div>
                      ) : (
                        <div className="product-price" style={{ fontSize: '13px', color: p.color ? getTextColor(p.color) : undefined }}>${p.price}</div>
                      )}
                    </div>
                  </>
                ) : (
                  // ── Icon/color button layout ──
                  <>
                    <span className="product-icon" style={textColor ? { color: textColor } : {}}>
                      {iconEntry
                        ? <iconEntry.C size={28} strokeWidth={1.5} color={textColor || undefined} />
                        : p.is_custom
                          ? <Palette size={28} strokeWidth={1.5} color={textColor || undefined} />
                          : p.stock_id
                            ? <Package size={28} strokeWidth={1.5} color={textColor || undefined} />
                            : <Tag size={28} strokeWidth={1.5} color={textColor || undefined} />
                      }
                    </span>
                    <span className="product-name" style={textColor ? { color: textColor } : {}}>{p.name}</span>
                    {p.is_custom ? (
                      <span className="product-price" style={{ fontSize: '11px', ...(textColor ? { color: textColor, opacity: 0.85 } : {}) }}>
                        Personalizado
                      </span>
                    ) : (
                      <span className="product-price" style={textColor ? { color: textColor, opacity: 0.9 } : {}}>
                        ${p.price}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
        {filteredProducts.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon"><Package size={48} strokeWidth={1} /></span>
            <p className="empty-text">No hay productos/servicios creados</p>
          </div>
        )}
        <div className="footer-logo">
          <img src={isEstetica ? "/tuki-logo-dark.svg" : "/tuki-logo.svg"} alt="TUKI" />
        </div>
      </div>

      {/* Regular Sale Modal */}
      {showSaleModal && (
        <div className="modal-overlay" onClick={() => setShowSaleModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">{selectedProduct?.name}</h3>
            <p className="accent-text" style={{ textAlign: 'center', marginBottom: '12px' }}>Precio unitario: ${selectedProduct?.price}</p>

            {/* Stock warning */}
            {stockWarning && (
              <div
                data-testid="stock-warning-banner"
                style={{
                  background: 'rgba(255,152,0,0.10)',
                  border: '1px solid #FF9800',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <AlertTriangle size={18} color="#FF9800" strokeWidth={1.5} />
                <div>
                  <div style={{ fontWeight: 700, color: '#FF9800', fontSize: '13px' }}>
                    Sin stock disponible
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 1 }}>
                    El stock de "{stockWarning.name}" está en 0. Podés registrar igual para control.
                  </div>
                </div>
              </div>
            )}

            {/* Quantity selector */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16, background: 'var(--surface)', borderRadius: 12, padding: '12px 16px' }}>
              <button
                onClick={() => setSaleQuantity(q => Math.max(1, q - 1))}
                style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
              >−</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 800 }}>{saleQuantity}</div>
                <div style={{ fontSize: '11px', color: 'var(--text2)' }}>unidades</div>
              </div>
              <button
                onClick={() => setSaleQuantity(q => q + 1)}
                style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid var(--accent)', background: 'rgba(93,230,200,0.1)', color: 'var(--accent)', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
              >+</button>
            </div>
            {saleQuantity > 1 && (
              <p style={{ textAlign: 'center', fontWeight: 700, color: 'var(--accent)', marginBottom: 16, fontSize: '16px' }}>
                Total: ${(selectedProduct?.price * saleQuantity).toLocaleString('es-AR')}
              </p>
            )}
            
            <label className="checkbox-row" onClick={() => setIsCompleted(!isCompleted)}>
              <div className={`checkbox-box ${isCompleted ? 'checked' : ''}`}>{isCompleted && <Check size={20} strokeWidth={3} />}</div>
              <span>REALIZADO</span>
            </label>
            <label className="checkbox-row" onClick={() => { setIsPaid(!isPaid); if (!isPaid) { setSelectedClient(null); setPaymentMethod(''); } }}>
              <div className={`checkbox-box ${isPaid ? 'checked' : ''}`}>{isPaid && <Check size={20} strokeWidth={3} />}</div>
              <span>PAGADO</span>
            </label>

            {isPaid && isCompleted && (
              <div className="payment-section">
                <p className="label-small">Medio de pago: *</p>
                {paymentAlert && (
                  <div className="payment-alert">
                    <AlertTriangle size={18} style={{ marginRight: '8px' }} /> Debes seleccionar una forma de pago
                  </div>
                )}
                <div className="payment-row">
                  <button className={`payment-btn ${paymentMethod === 'transferencia' ? 'active' : ''}`} onClick={() => setPaymentMethod('transferencia')}><Smartphone size={16} /> Transferencia</button>
                  <button className={`payment-btn ${paymentMethod === 'efectivo' ? 'active' : ''}`} onClick={() => setPaymentMethod('efectivo')}><Banknote size={16} /> Efectivo</button>
                </div>
              </div>
            )}

            {!isPaid && isCompleted && (
              <div className="client-selection">
                <p className="label-small">Selecciona el cliente (Cuenta Corriente):</p>
                <div className="clients-list-scroll">
                  {clients.map(c => (
                    <button key={c.id} className={`client-item ${selectedClient === c.id ? 'active' : ''}`} onClick={() => setSelectedClient(c.id)}>{c.full_name}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-buttons" style={{ marginTop: '20px' }}>
              <button className="btn-secondary" onClick={() => setShowSaleModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleRegisterSale} disabled={loading}>{loading ? 'Registrando...' : 'REGISTRADO'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Work Modal */}
      {showCustomModal && (
        <div className="modal-overlay" onClick={() => setShowCustomModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh' }}>
            <h3 className="modal-title">🎨 {selectedProduct?.name}</h3>
            
            {/* Client Selection - Required */}
            <div className="form-group">
              <label className="label">Cliente *</label>
              <select className="input" value={customForm.client_id} onChange={e => setCustomForm({...customForm, client_id: e.target.value})} required>
                <option value="">Seleccionar cliente</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>

            {/* Description - Optional */}
            <div className="form-group">
              <label className="label">Descripción del trabajo</label>
              <textarea className="input" rows={2} value={customForm.description} onChange={e => setCustomForm({...customForm, description: e.target.value})} placeholder="Describe el trabajo realizado..." />
            </div>

            {/* Price - Required */}
            <div className="form-group">
              <label className="label">Precio *</label>
              <input className="input" type="number" step="0.01" value={customForm.price} onChange={e => setCustomForm({...customForm, price: e.target.value})} placeholder="0.00" required />
            </div>

            {/* Cost - Optional */}
            <div className="form-group">
              <label className="label">Costo (opcional)</label>
              <input className="input" type="number" step="0.01" value={customForm.cost} onChange={e => setCustomForm({...customForm, cost: e.target.value})} placeholder="0.00" />
            </div>

            {/* Stock Items - Optional */}
            {stockItems.length > 0 && (
              <div className="form-group">
                <label className="label">📦 Productos del Stock utilizados</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {stockItems.map(s => (
                    <button key={s.id} type="button" className={`stock-tag ${customForm.stock_ids.includes(s.id) ? 'active' : ''}`} onClick={() => toggleStockItem(s.id)}>
                      {s.description} ({s.quantity})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Photos - Optional */}
            <div className="form-group">
              <label className="label"><Camera size={16} style={{ marginRight: '6px' }} /> Fotos del trabajo</label>
              <input type="file" ref={fileInputRef} accept="image/*" multiple onChange={handlePhotoUpload} style={{ display: 'none' }} />
              <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Plus size={16} /> Agregar fotos
              </button>
              {customForm.photos.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', overflowX: 'auto' }}>
                  {customForm.photos.map((photo, i) => (
                    <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                      <img src={photo} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                      <button onClick={() => removePhoto(i)} style={{ position: 'absolute', top: '-8px', right: '-8px', background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Method - Required */}
            {/* Realizado / Pagado checkboxes */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginBottom: 8 }}>
              <label className="checkbox-row" onClick={() => setIsCompleted(!isCompleted)}>
                <div className={`checkbox-box ${isCompleted ? 'checked' : ''}`}>{isCompleted && <Check size={20} strokeWidth={3} />}</div>
                <span>REALIZADO</span>
              </label>
              <label className="checkbox-row" style={{ marginTop: 8 }} onClick={() => { setIsPaid(!isPaid); if (isPaid) setPaymentMethod(''); }}>
                <div className={`checkbox-box ${isPaid ? 'checked' : ''}`}>{isPaid && <Check size={20} strokeWidth={3} />}</div>
                <span>PAGADO</span>
              </label>
            </div>

            {/* Payment method — only when Pagado */}
            {isPaid && isCompleted && (
              <div className="payment-section">
                <p className="label">Forma de pago *</p>
                {paymentAlert && (
                  <div className="payment-alert">
                    <AlertTriangle size={18} style={{ marginRight: '8px' }} /> Debes seleccionar una forma de pago
                  </div>
                )}
                <div className="payment-row">
                  <button className={`payment-btn ${paymentMethod === 'efectivo' ? 'active' : ''}`} onClick={() => setPaymentMethod('efectivo')}><Banknote size={16} /> Efectivo</button>
                  <button className={`payment-btn ${paymentMethod === 'transferencia' ? 'active' : ''}`} onClick={() => setPaymentMethod('transferencia')}><Smartphone size={16} /> Transferencia</button>
                </div>
              </div>
            )}

            {/* Cuenta corriente hint */}
            {!isPaid && isCompleted && (
              <div style={{ fontSize: '12px', color: 'var(--text2)', background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                Se registrará como cuenta corriente. El pago queda pendiente de cobro.
              </div>
            )}

            <div className="modal-buttons" style={{ marginTop: '16px' }}>
              <button className="btn-secondary" onClick={() => setShowCustomModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleRegisterCustomWork} disabled={loading}>{loading ? 'Registrando...' : 'REGISTRAR TRABAJO'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
